/**
 * StyledPopupShell — shared visual shell for app popups so they don't look like
 * "plain text in a white box". Wraps Radix Dialog with a gradient header band,
 * a circular icon medallion, and consistent typography + CTA slots.
 *
 * Keep behavior in the caller (open state, popupQueue integration, onClose).
 */
import { ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

type Accent = "brand" | "emerald" | "amber" | "sky" | "rose";

const accentMap: Record<Accent, { grad: string; ring: string; icon: string }> = {
  brand:   { grad: "from-primary/25 via-primary/10 to-transparent", ring: "ring-primary/30",  icon: "bg-primary/15 text-primary" },
  emerald: { grad: "from-emerald-400/25 via-emerald-300/10 to-transparent", ring: "ring-emerald-400/30", icon: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300" },
  amber:   { grad: "from-amber-400/30 via-amber-300/10 to-transparent",     ring: "ring-amber-400/30",   icon: "bg-amber-500/15 text-amber-600 dark:text-amber-300" },
  sky:     { grad: "from-sky-400/25 via-sky-300/10 to-transparent",         ring: "ring-sky-400/30",     icon: "bg-sky-500/15 text-sky-600 dark:text-sky-300" },
  rose:    { grad: "from-rose-400/25 via-rose-300/10 to-transparent",       ring: "ring-rose-400/30",    icon: "bg-rose-500/15 text-rose-600 dark:text-rose-300" },
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  accent?: Accent;
  children?: ReactNode;
  footer?: ReactNode;
  className?: string;
  hideCloseButton?: boolean;
}

export function StyledPopupShell({
  open,
  onOpenChange,
  icon,
  title,
  description,
  accent = "brand",
  children,
  footer,
  className,
  hideCloseButton,
}: Props) {
  const a = accentMap[accent];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "sm:max-w-md p-0 overflow-hidden border-none shadow-2xl",
          hideCloseButton && "[&>button[aria-label=Close]]:hidden",
          className,
        )}
      >
        {/* Gradient header band */}
        <div className={cn("relative px-6 pt-7 pb-6 bg-gradient-to-b", a.grad)}>
          <div aria-hidden className="absolute inset-x-0 -top-16 h-32 blur-3xl opacity-60 pointer-events-none">
            <div className={cn("mx-auto h-full w-2/3 rounded-full bg-gradient-to-b", a.grad)} />
          </div>
          {icon && (
            <div className={cn(
              "relative mx-auto mb-3 h-14 w-14 rounded-full flex items-center justify-center ring-4 ring-background",
              a.icon,
            )}>
              <div className={cn("absolute inset-0 rounded-full ring-2", a.ring)} />
              {icon}
            </div>
          )}
          <DialogHeader className="text-center space-y-1">
            <DialogTitle className="text-xl sm:text-2xl font-bold tracking-tight">
              {title}
            </DialogTitle>
            {description && (
              <DialogDescription className="text-sm text-muted-foreground max-w-sm mx-auto">
                {description}
              </DialogDescription>
            )}
          </DialogHeader>
        </div>

        {/* Body */}
        {children && (
          <div className="px-6 pb-4 space-y-3">
            {children}
          </div>
        )}

        {footer && (
          <div className="px-6 pb-6 pt-2 flex flex-col sm:flex-row gap-2 sm:justify-end">
            {footer}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
